// pages/AnalyticsPage.jsx — rebuilt 29 July 2026
//
// The old page answered "how many things happened". This one answers the four
// questions a broker owner actually opens analytics to ask:
//   1. Did we make money, and is it banked or still hope?      → KPI row + revenue split
//   2. Which lead source is worth the effort?                  → Source ROI table
//   3. Who on my team is producing?                            → Team leaderboard
//   4. What is stuck and needs me today?                       → Alerts + pipeline ageing
//
// Owner-first. Managers get the same page scoped to their team; advisers get
// their own deals with the team panel hidden (the API returns team: [] for them).

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import Sidebar from '../components/Sidebar';
import { useMobile } from '../hooks/useMobile';

const token = () => localStorage.getItem('token');

/* ── palette ──────────────────────────────────────────────────────────── */
const C = {
  ink:      '#0e1b2c',
  ink70:    '#4b5a6e',
  muted:    '#7c8b9e',
  faint:    '#a8b4c2',
  line:     '#E6EBF1',
  bg:       '#F5F7FA',
  card:     '#ffffff',
  green:    '#0ea472',
  greenBg:  '#e7f7f1',
  indigo:   '#5b62f4',
  indigoBg: '#eeeffe',
  amber:    '#e08700',
  amberBg:  '#fdf3e2',
  red:      '#dc2b3f',
  redBg:    '#fdeced',
  sky:      '#0b8fd4',
  skyBg:    '#e6f4fc',
};

/* ── formatting ───────────────────────────────────────────────────────── */
const gbp = (v, dp = 0) =>
  '£' + Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });

// Compact form for chart axes and tight cards: £12.4k rather than £12,400.
const gbpShort = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1000000) return '£' + (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'm';
  if (Math.abs(n) >= 1000)    return '£' + (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return '£' + Math.round(n);
};
const pct  = (v) => `${Number(v || 0).toFixed(Number(v) % 1 === 0 ? 0 : 1)}%`;
const nInt = (v) => Number(v || 0).toLocaleString('en-GB');

/* ── period presets ───────────────────────────────────────────────────── */
const iso = (d) => d.toISOString().slice(0, 10);
const shift = (days) => { const d = new Date(); d.setDate(d.getDate() - days); return iso(d); };

const PRESETS = [
  { key: '7d',  label: '7 days',   from: () => shift(6),   to: () => iso(new Date()) },
  { key: '30d', label: '30 days',  from: () => shift(29),  to: () => iso(new Date()) },
  { key: '90d', label: '90 days',  from: () => shift(89),  to: () => iso(new Date()) },
  { key: 'ytd', label: 'Year to date',
    from: () => `${new Date().getFullYear()}-01-01`, to: () => iso(new Date()) },
  { key: '12m', label: '12 months', from: () => shift(364), to: () => iso(new Date()) },
];

/* ── small building blocks ────────────────────────────────────────────── */

function Delta({ value, invert = false, suffix = '' }) {
  // null = no prior baseline to compare against. Saying "+∞%" because last
  // month was zero is worse than saying nothing.
  if (value === null || value === undefined) {
    return <span style={{ fontSize: 12, color: C.faint, fontWeight: 500 }}>no prior data</span>;
  }
  const flat = Math.abs(value) < 0.05;
  const good = invert ? value < 0 : value > 0;
  const colour = flat ? C.muted : good ? C.green : C.red;
  const bg     = flat ? '#F1F4F8' : good ? C.greenBg : C.redBg;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, fontWeight: 600,
      color: colour, background: bg, padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap',
    }}>
      {flat ? '–' : good ? '▲' : '▼'} {flat ? '0%' : `${Math.abs(value)}%`}{suffix}
    </span>
  );
}

function Card({ children, style, pad = 22 }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: pad,
      boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.03)',
      minWidth: 0, ...style,
    }}>{children}</div>
  );
}

function CardHead({ title, hint, right }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
      <div style={{ minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>{title}</h2>
        {hint && <p style={{ margin: '3px 0 0', fontSize: 12.5, color: C.muted, lineHeight: 1.4 }}>{hint}</p>}
      </div>
      {right}
    </div>
  );
}

function Kpi({ label, value, sub, delta, invert, accent = C.ink, tag }) {
  return (
    <Card pad={18} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </span>
        {tag && (
          <span style={{ fontSize: 9.5, fontWeight: 700, color: C.sky, background: C.skyBg,
                         padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {tag}
          </span>
        )}
      </div>
      <div style={{ fontSize: 27, fontWeight: 700, color: accent, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 20 }}>
        {delta !== undefined && <Delta value={delta} invert={invert} />}
        {sub && <span style={{ fontSize: 12, color: C.faint }}>{sub}</span>}
      </div>
    </Card>
  );
}

function Empty({ children }) {
  return (
    <div style={{ padding: '28px 8px', textAlign: 'center', color: C.faint, fontSize: 13.5 }}>
      {children}
    </div>
  );
}

function Bar2({ value, max, colour, height = 7 }) {
  const w = max > 0 ? Math.max(value > 0 ? 3 : 0, (value / max) * 100) : 0;
  return (
    <div style={{ background: '#EEF1F5', borderRadius: 20, height, width: '100%', overflow: 'hidden' }}>
      <div style={{ width: `${w}%`, height: '100%', background: colour, borderRadius: 20, transition: 'width .3s ease' }} />
    </div>
  );
}

const th = { textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase',
             letterSpacing: '0.04em', padding: '0 10px 9px 0', whiteSpace: 'nowrap' };
const td = { fontSize: 13.5, color: C.ink70, padding: '11px 10px 11px 0', borderTop: `1px solid ${C.line}`, verticalAlign: 'middle' };

/* ── chart tooltip ────────────────────────────────────────────────────── */
function ChartTip({ active, payload, label, moneyKeys = [] }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 10, padding: '10px 12px',
                  boxShadow: '0 4px 14px rgba(16,24,40,0.10)', fontSize: 12.5 }}>
      <div style={{ fontWeight: 700, color: C.ink, marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flexShrink: 0 }} />
          <span style={{ color: C.ink70 }}>{p.name}</span>
          <strong style={{ color: C.ink, marginLeft: 'auto' }}>
            {moneyKeys.includes(p.dataKey) ? gbp(p.value) : nInt(p.value)}
          </strong>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */

export default function AnalyticsPage() {
  const mobile = useMobile();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefresh]= useState(false);
  const [error, setError]       = useState('');
  const [preset, setPreset]     = useState('90d');
  const [from, setFrom]         = useState(PRESETS[2].from());
  const [to, setTo]             = useState(PRESETS[2].to());
  const [inclArchived, setIncl] = useState(false);
  const [exporting, setExport]  = useState(false);

  const load = useCallback((isRefresh) => {
    isRefresh ? setRefresh(true) : setLoading(true);
    setError('');
    const qs = new URLSearchParams({ from, to, include_archived: String(inclArchived) });
    fetch(`/api/broker/analytics?${qs}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => { if (!r.ok) throw new Error('http'); return r.json(); })
      .then(d => { if (d.error) throw new Error(d.error); setData(d); })
      .catch(() => setError('Could not load analytics. Please try again.'))
      .finally(() => { setLoading(false); setRefresh(false); });
  }, [from, to, inclArchived]);

  useEffect(() => { load(false); }, [load]);

  const applyPreset = (p) => {
    setPreset(p.key);
    setFrom(p.from());
    setTo(p.to());
  };

  const exportCsv = async () => {
    setExport(true);
    try {
      const qs = new URLSearchParams({ from, to, include_archived: String(inclArchived) });
      const r  = await fetch(`/api/exports/analytics?${qs}`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!r.ok) throw new Error('export failed');
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `analytics-${from}-to-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Export failed. Please try again.');
    } finally {
      setExport(false);
    }
  };

  /* ── derived ──────────────────────────────────────────────────────── */
  const k        = data?.kpis;
  const sources  = data?.sources || [];
  const maxSrcLeads = useMemo(() => Math.max(1, ...sources.map(s => s.leads)), [sources]);
  const team     = data?.team || [];
  const maxTeamRev = useMemo(
    () => Math.max(1, ...team.map(t => t.revenueBanked + t.revenuePipeline)), [team]);
  const funnel   = data?.funnel || [];
  const funnelMax= funnel.length ? Math.max(1, funnel[0].count) : 1;
  const stages   = data?.pipeline?.stages || [];
  const maxStage = useMemo(() => Math.max(1, ...stages.map(s => s.deals)), [stages]);

  const ml = mobile ? 0 : 70;
  const pt = mobile ? 64 : 0;
  const pad = mobile ? 14 : 32;

  /* ── loading / error shells ───────────────────────────────────────── */
  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: C.bg }}>
        <Sidebar />
        <div style={{ flex: 1, marginLeft: ml, paddingTop: pt, display: 'flex',
                      alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', color: C.muted }}>
            <div style={{
              width: 30, height: 30, border: `3px solid ${C.line}`, borderTopColor: C.indigo,
              borderRadius: '50%', margin: '0 auto 12px', animation: 'bcspin 0.8s linear infinite',
            }} />
            <div style={{ fontSize: 13.5 }}>Crunching your numbers…</div>
          </div>
          <style>{`@keyframes bcspin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="anx-analytics" style={{ display: 'flex', minHeight: '100vh', background: C.bg }}>
      <Sidebar />
      {/* Every rule is scoped under .anx-analytics on purpose.
          create-react-app compiles ALL imported CSS into one global stylesheet,
          so a generic class name here is a live grenade: `.bc-row { display:flex }`
          in components/UserManager.css was silently applied to this page's table
          rows and collapsed every column out of alignment with its header.
          Scoping under the page wrapper means no other page's CSS can reach in,
          and nothing here can leak out. Do not de-scope these. */}
      <style>{`
        @keyframes bcspin{to{transform:rotate(360deg)}}
        .anx-analytics .anx-chip{cursor:pointer;transition:all .15s ease}
        .anx-analytics .anx-chip:hover{border-color:#c3cbd6}
        .anx-analytics .anx-btn{cursor:pointer;transition:all .15s ease}
        .anx-analytics .anx-btn:hover{filter:brightness(1.06)}
        .anx-analytics tbody tr:hover{background:#FAFBFD}
        .anx-analytics .anx-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch}
        .anx-analytics table{width:100%;border-collapse:collapse}
      `}</style>

      <div style={{ flex: 1, marginLeft: ml, paddingTop: pt, minWidth: 0 }}>

        {/* ── Header ───────────────────────────────────────────────── */}
        <div style={{ background: C.card, borderBottom: `1px solid ${C.line}`, padding: `18px ${pad}px 0` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                        gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0, fontSize: mobile ? 20 : 25, fontWeight: 700,
                             color: C.ink, letterSpacing: '-0.02em' }}>Analytics</h1>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.indigo, background: C.indigoBg,
                               padding: '3px 9px', borderRadius: 20, textTransform: 'uppercase',
                               letterSpacing: '0.04em' }}>
                  {data?.scopeLabel}
                </span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>
                {from} → {to} · compared with {data?.period?.compareFrom} → {data?.period?.compareTo}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="anx-btn" onClick={() => load(true)} disabled={refreshing}
                style={{ background: '#fff', color: C.ink70, border: `1px solid ${C.line}`, borderRadius: 9,
                         padding: '8px 13px', fontSize: 13, fontWeight: 600 }}>
                {refreshing ? 'Refreshing…' : '↻ Refresh'}
              </button>
              <button className="anx-btn" onClick={exportCsv} disabled={exporting}
                style={{ background: C.ink, color: '#fff', border: 'none', borderRadius: 9,
                         padding: '8px 15px', fontSize: 13, fontWeight: 600 }}>
                {exporting ? 'Preparing…' : '↓ Export CSV'}
              </button>
            </div>
          </div>

          {/* Period controls */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingBottom: 14 }}>
            {PRESETS.map(p => {
              const on = preset === p.key;
              return (
                <button key={p.key} className="anx-chip" onClick={() => applyPreset(p)}
                  style={{
                    background: on ? C.ink : '#fff', color: on ? '#fff' : C.ink70,
                    border: `1px solid ${on ? C.ink : C.line}`, borderRadius: 8,
                    padding: '7px 13px', fontSize: 13, fontWeight: 600,
                  }}>{p.label}</button>
              );
            })}
            <span style={{ width: 1, height: 22, background: C.line, margin: '0 3px' }} />
            <input type="date" value={from}
              onChange={e => { setFrom(e.target.value); setPreset('custom'); }}
              style={{ padding: '6px 9px', border: `1px solid ${C.line}`, borderRadius: 8,
                       fontSize: 13, color: C.ink, background: '#fff' }} />
            <span style={{ fontSize: 13, color: C.faint }}>to</span>
            <input type="date" value={to}
              onChange={e => { setTo(e.target.value); setPreset('custom'); }}
              style={{ padding: '6px 9px', border: `1px solid ${C.line}`, borderRadius: 8,
                       fontSize: 13, color: C.ink, background: '#fff' }} />

            {data?.meta?.archivedLeadsInPeriod > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                              color: C.muted, cursor: 'pointer', marginLeft: 4 }}>
                <input type="checkbox" checked={inclArchived}
                       onChange={e => setIncl(e.target.checked)}
                       style={{ cursor: 'pointer', accentColor: C.indigo }} />
                Include {data.meta.archivedLeadsInPeriod} archived
              </label>
            )}
          </div>
        </div>

        <div style={{ padding: `${mobile ? 16 : 26}px ${pad}px 60px`, maxWidth: 1360 }}>

          {error && (
            <div style={{ background: C.redBg, border: `1px solid #f6c6cb`, color: C.red,
                          borderRadius: 10, padding: '11px 15px', fontSize: 13.5, marginBottom: 18 }}>
              {error}
            </div>
          )}

          {/* ── Alerts ─────────────────────────────────────────────── */}
          {data?.alerts?.length > 0 && (
            <div style={{
              display: 'grid', gap: 10, marginBottom: 22,
              gridTemplateColumns: mobile ? '1fr' : `repeat(auto-fit, minmax(250px, 1fr))`,
            }}>
              {data.alerts.map(a => {
                const tone = a.severity === 'high' ? { c: C.red, bg: C.redBg, b: '#f6c6cb' }
                          : a.severity === 'medium' ? { c: C.amber, bg: C.amberBg, b: '#f3ddb4' }
                          : { c: C.sky, bg: C.skyBg, b: '#c3e2f4' };
                return (
                  <a key={a.key} href={a.link} style={{
                    display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none',
                    background: tone.bg, border: `1px solid ${tone.b}`, borderRadius: 12, padding: '13px 15px',
                  }}>
                    <span style={{ fontSize: 22, fontWeight: 700, color: tone.c, lineHeight: 1 }}>{a.count}</span>
                    <span style={{ fontSize: 13, color: C.ink70, lineHeight: 1.35, flex: 1 }}>{a.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: tone.c, whiteSpace: 'nowrap' }}>
                      {a.action} →
                    </span>
                  </a>
                );
              })}
            </div>
          )}

          {/* ── KPI row ────────────────────────────────────────────── */}
          <div style={{
            display: 'grid', gap: 14, marginBottom: 22,
            gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px, 1fr))',
          }}>
            <Kpi label="Revenue banked" value={gbp(k?.revenueBanked.value)}
                 delta={k?.revenueBanked.delta} accent={C.green}
                 sub={`${nInt(k?.dealsCompleted.value)} completed`} />
            <Kpi label="Net after commission" value={gbp(k?.netRevenue.value)}
                 delta={k?.netRevenue.delta}
                 sub={data?.revenue?.introCommission ? `−${gbp(data.revenue.introCommission)} paid out` : 'nothing paid out'} />
            <Kpi label="Open pipeline" value={gbp(k?.openPipeline.value)} tag="live"
                 accent={C.amber} sub={`${nInt(k?.openPipeline.deals)} open deals`} />
            <Kpi label="Leads received" value={nInt(k?.leads.value)}
                 delta={k?.leads.delta} accent={C.indigo}
                 sub={`${nInt(data?.leads?.introducerTotal)} intro · ${nInt(data?.leads?.directTotal)} direct`} />
            <Kpi label="Conversion" value={pct(k?.conversionRate.value)}
                 delta={k?.conversionRate.delta} accent={C.sky}
                 sub={`${nInt(data?.leads?.converted)} of ${nInt(data?.leads?.total)} leads`} />
            <Kpi label="Avg deal value" value={gbp(k?.avgDealValue.value)}
                 delta={k?.avgDealValue.delta}
                 sub={k?.dealsCompleted.value ? 'completed deals' : 'no completions yet'} />
          </div>

          {/* ── Trend + Funnel ─────────────────────────────────────── */}
          <div style={{ display: 'grid', gap: 16, marginBottom: 22,
                        gridTemplateColumns: mobile ? '1fr' : 'minmax(0,1.75fr) minmax(0,1fr)' }}>

            <Card>
              <CardHead
                title="Leads and revenue over time"
                hint="Bars are leads by the date they arrived. The line is revenue banked, dated by completion."
              />
              <div style={{ height: 265 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data?.trend || []} margin={{ top: 4, right: 6, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.line} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.muted }}
                           axisLine={{ stroke: C.line }} tickLine={false}
                           interval="preserveStartEnd" minTickGap={16} />
                    <YAxis yAxisId="l" tick={{ fontSize: 11, fill: C.muted }} axisLine={false}
                           tickLine={false} allowDecimals={false} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: C.muted }}
                           axisLine={false} tickLine={false} tickFormatter={gbpShort} width={52} />
                    <Tooltip content={<ChartTip moneyKeys={['revenueBanked']} />} />
                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} iconType="circle" iconSize={8} />
                    {/* isAnimationActive={false} on purpose: the entry animation
                        replays on every filter change, which is noise on a
                        dashboard you re-filter constantly — and recharts renders
                        no bar rectangles at all until the animation starts, so
                        the chart is empty in any non-animating context. */}
                    <Bar yAxisId="l" dataKey="leadsIntroducer" name="Introducer leads"
                         stackId="a" fill={C.indigo} maxBarSize={38} isAnimationActive={false} />
                    <Bar yAxisId="l" dataKey="leadsDirect" name="Direct leads"
                         stackId="a" fill="#9aa0f8" radius={[4, 4, 0, 0]} maxBarSize={38}
                         isAnimationActive={false} />
                    <Line yAxisId="r" type="monotone" dataKey="revenueBanked" name="Revenue banked"
                          stroke={C.green} strokeWidth={2.5} dot={{ r: 3, fill: C.green }}
                          isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card>
              <CardHead title="Lead to cash" hint="Where people fall out between arriving and completing." />
              {funnel[0]?.count === 0 ? (
                <Empty>No leads in this period.</Empty>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {funnel.map((f, i) => {
                    const prevCount = i > 0 ? funnel[i - 1].count : null;
                    const dropped   = prevCount !== null ? prevCount - f.count : 0;
                    const share     = funnelMax ? (f.count / funnelMax) * 100 : 0;
                    const tones     = [C.indigo, C.sky, C.green, C.ink];
                    return (
                      <div key={f.key}>
                        {i > 0 && dropped > 0 && (
                          <div style={{ fontSize: 11.5, color: C.red, padding: '3px 0 5px 2px', fontWeight: 600 }}>
                            ↓ {nInt(dropped)} lost here
                            {prevCount ? ` (${Math.round((dropped / prevCount) * 100)}%)` : ''}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between',
                                      alignItems: 'baseline', marginBottom: 5 }}>
                          <span style={{ fontSize: 13, color: C.ink70, fontWeight: 500 }}>{f.label}</span>
                          <span style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{nInt(f.count)}</span>
                        </div>
                        <div style={{ background: '#EEF1F5', borderRadius: 6, height: 24, overflow: 'hidden' }}>
                          <div style={{
                            width: `${Math.max(f.count > 0 ? 2 : 0, share)}%`, height: '100%',
                            background: tones[i % tones.length], borderRadius: 6, transition: 'width .3s ease',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.line}`,
                                fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>
                    Overall <strong style={{ color: C.ink }}>{pct(k?.conversionRate.value)}</strong> of
                    leads received became a deal.
                    {data?.velocity?.sampleLeadToDeal > 0 && (
                      <> Typically <strong style={{ color: C.ink }}>
                        {data.velocity.medianLeadToDays}
                      </strong> days from lead to deal.</>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* ── Source ROI ─────────────────────────────────────────── */}
          <Card style={{ marginBottom: 22 }}>
            <CardHead
              title="Which lead sources actually pay"
              hint="Every channel side by side: how many leads it sent, how many became deals, and what that was worth."
            />
            {sources.length === 0 ? (
              <Empty>No leads in this period, so there's nothing to compare yet.</Empty>
            ) : (
              <div className="anx-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: '26%' }}>Source</th>
                      <th style={{ ...th, width: '22%' }}>Leads</th>
                      <th style={th}>Converted</th>
                      <th style={th}>Conv. rate</th>
                      <th style={{ ...th, textAlign: 'right' }}>Banked</th>
                      <th style={{ ...th, textAlign: 'right' }}>In pipeline</th>
                      <th style={{ ...th, textAlign: 'right' }}>Per lead</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map(s => (
                      <tr key={s.key}>
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: C.ink }}>{s.label}</div>
                          <div style={{ fontSize: 11.5, color: C.faint }}>{s.group}</div>
                        </td>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                            <span style={{ fontWeight: 700, color: C.ink, minWidth: 22 }}>{nInt(s.leads)}</span>
                            <div style={{ flex: 1, minWidth: 40 }}>
                              <Bar2 value={s.leads} max={maxSrcLeads} colour={C.indigo} />
                            </div>
                          </div>
                        </td>
                        <td style={td}>{nInt(s.converted)}</td>
                        <td style={td}>
                          <span style={{
                            fontWeight: 700,
                            color: s.conversionRate >= 40 ? C.green
                                 : s.conversionRate >= 15 ? C.amber : C.muted,
                          }}>{pct(s.conversionRate)}</span>
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: s.revenueBanked ? C.green : C.faint }}>
                          {gbp(s.revenueBanked)}
                        </td>
                        <td style={{ ...td, textAlign: 'right', color: s.revenuePipeline ? C.amber : C.faint }}>
                          {gbp(s.revenuePipeline)}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>{gbp(s.revenuePerLead)}</td>
                      </tr>
                    ))}
                    {(data?.unattributed?.pipelineDeals > 0 || data?.unattributed?.bankedDeals > 0) && (
                      <tr>
                        <td style={{ ...td, color: C.faint, fontStyle: 'italic' }}>
                          Added directly
                          <div style={{ fontSize: 11.5, fontStyle: 'normal' }}>no originating lead</div>
                        </td>
                        <td style={{ ...td, color: C.faint }}>—</td>
                        <td style={{ ...td, color: C.faint }}>{nInt(data.unattributed.bankedDeals)}</td>
                        <td style={{ ...td, color: C.faint }}>—</td>
                        <td style={{ ...td, textAlign: 'right', color: C.faint }}>{gbp(data.unattributed.bankedValue)}</td>
                        <td style={{ ...td, textAlign: 'right', color: C.faint }}>{gbp(data.unattributed.pipelineValue)}</td>
                        <td style={{ ...td, textAlign: 'right', color: C.faint }}>—</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* ── Team ───────────────────────────────────────────────── */}
          {team.length > 0 && (
            <Card style={{ marginBottom: 22 }}>
              <CardHead
                title={data.scope === 'manager' ? 'Your team' : 'Who is producing'}
                hint="Leads handled, what converted, and the revenue behind it. Sorted by revenue banked."
              />
              <div className="anx-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: '24%' }}>Person</th>
                      <th style={th}>Leads</th>
                      <th style={th}>Converted</th>
                      <th style={th}>Conv. rate</th>
                      <th style={th}>Completed</th>
                      <th style={{ ...th, width: '20%', textAlign: 'right' }}>Revenue</th>
                      <th style={{ ...th, textAlign: 'right' }}>Open now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...team]
                      .sort((a, b) => (b.revenueBanked + b.revenuePipeline) - (a.revenueBanked + a.revenuePipeline)
                                   || b.leadsAssigned - a.leadsAssigned)
                      .map(t => (
                      <tr key={t.id}>
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: C.ink }}>{t.name}</div>
                          <div style={{ fontSize: 11.5, color: C.faint, textTransform: 'capitalize' }}>
                            {t.level}{t.status !== 'active' ? ` · ${t.status}` : ''}
                          </div>
                        </td>
                        <td style={td}>{nInt(t.leadsAssigned)}</td>
                        <td style={td}>{nInt(t.leadsConverted)}</td>
                        <td style={td}>
                          <span style={{ fontWeight: 700, color: t.leadsAssigned === 0 ? C.faint
                                       : t.conversionRate >= 40 ? C.green
                                       : t.conversionRate >= 15 ? C.amber : C.muted }}>
                            {t.leadsAssigned === 0 ? '—' : pct(t.conversionRate)}
                          </span>
                        </td>
                        <td style={td}>{nInt(t.dealsCompleted)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <div style={{ fontWeight: 700, color: C.ink }}>
                            {gbp(t.revenueBanked)}
                            {t.revenuePipeline > 0 && (
                              <span style={{ fontWeight: 500, color: C.amber, fontSize: 12 }}>
                                {' '}+{gbp(t.revenuePipeline)} open
                              </span>
                            )}
                          </div>
                          <div style={{ marginTop: 5 }}>
                            <Bar2 value={t.revenueBanked + t.revenuePipeline} max={maxTeamRev}
                                  colour={t.revenueBanked > 0 ? C.green : C.amber} height={5} />
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>{nInt(t.openLeadsNow)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* ── Pipeline health + Revenue breakdown ────────────────── */}
          <div style={{ display: 'grid', gap: 16, marginBottom: 22,
                        gridTemplateColumns: mobile ? '1fr' : 'minmax(0,1.35fr) minmax(0,1fr)' }}>

            <Card>
              <CardHead
                title="Pipeline health"
                hint="Open deals by stage, and how long they have been sitting there."
                right={<span style={{ fontSize: 13, color: C.muted, whiteSpace: 'nowrap' }}>
                  <strong style={{ color: C.ink }}>{gbp(data?.pipeline?.openValue)}</strong> open
                </span>}
              />
              {data?.pipeline?.openDeals === 0 ? (
                <Empty>No open deals right now.</Empty>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
                  {stages.map(s => (
                    <div key={s.name}>
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'baseline', gap: 10, marginBottom: 5 }}>
                        <span style={{ fontSize: 13, color: C.ink70, fontWeight: 500 }}>{s.name}</span>
                        <span style={{ fontSize: 12.5, color: C.muted, whiteSpace: 'nowrap' }}>
                          <strong style={{ color: C.ink }}>{nInt(s.deals)}</strong>
                          {s.value > 0 && ` · ${gbp(s.value)}`}
                          {s.deals > 0 && (
                            <span style={{ color: s.maxAgeDays > 30 ? C.red : s.maxAgeDays > 14 ? C.amber : C.faint }}>
                              {' '}· {s.avgAgeDays < 1 ? '<1' : Math.round(s.avgAgeDays)}d avg
                            </span>
                          )}
                        </span>
                      </div>
                      <Bar2 value={s.deals} max={maxStage}
                            colour={s.maxAgeDays > 30 ? C.red : s.maxAgeDays > 14 ? C.amber : C.indigo} />
                    </div>
                  ))}
                  {data?.velocity?.sampleCompletion > 0 && (
                    <div style={{ marginTop: 4, paddingTop: 12, borderTop: `1px solid ${C.line}`,
                                  fontSize: 12.5, color: C.muted }}>
                      Deals take on average <strong style={{ color: C.ink }}>
                        {data.velocity.avgDealToCompletion}
                      </strong> days from creation to completion
                      <span style={{ color: C.faint }}> ({data.velocity.sampleCompletion} completed)</span>.
                    </div>
                  )}
                </div>
              )}
            </Card>

            <Card>
              <CardHead title="Where the money went" hint="Banked revenue for this period, broken down." />
              {[
                { label: 'Lender proc fees', value: data?.revenue?.procFees,        colour: C.green },
                { label: 'Client fees',      value: data?.revenue?.clientFees,      colour: C.sky },
                { label: 'Introducer commission', value: -(data?.revenue?.introCommission || 0), colour: C.red },
                { label: 'Network fees',     value: -(data?.revenue?.networkFees || 0), colour: C.red },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between',
                                            alignItems: 'center', padding: '11px 0',
                                            borderBottom: `1px solid ${C.line}` }}>
                  <span style={{ fontSize: 13.5, color: C.ink70 }}>{r.label}</span>
                  <span style={{ fontSize: 14.5, fontWeight: 700,
                                 color: r.value < 0 ? C.red : r.value > 0 ? r.colour : C.faint }}>
                    {r.value < 0 ? '−' : ''}{gbp(Math.abs(r.value))}
                  </span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '14px 0 4px' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>Net kept</span>
                <span style={{ fontSize: 19, fontWeight: 700, color: C.ink }}>
                  {gbp(data?.revenue?.net)}
                </span>
              </div>
              <div style={{ marginTop: 14, padding: '11px 13px', background: C.amberBg,
                            borderRadius: 9, fontSize: 12.5, color: C.ink70, lineHeight: 1.5 }}>
                <strong style={{ color: C.ink }}>{gbp(data?.revenue?.openPipeline)}</strong> is still
                in the pipeline across {nInt(data?.revenue?.openPipelineCount)} open{' '}
                {data?.revenue?.openPipelineCount === 1 ? 'deal' : 'deals'} — not yet earned.
                {data?.revenue?.lostCount > 0 && (
                  <> {gbp(data.revenue.lost)} was lost on {nInt(data.revenue.lostCount)} cancelled{' '}
                  {data.revenue.lostCount === 1 ? 'deal' : 'deals'}.</>
                )}
              </div>
            </Card>
          </div>

          {/* ── Introducers + Lost reasons ─────────────────────────── */}
          <div style={{ display: 'grid', gap: 16,
                        gridTemplateColumns: mobile ? '1fr' : 'minmax(0,1.6fr) minmax(0,1fr)' }}>

            <Card>
              <CardHead
                title="Introducer league table"
                hint="Ranked by revenue they generated for you — not by what you owe them."
              />
              {(data?.introducers || []).length === 0 ? (
                <Empty>No introducer activity in this period.</Empty>
              ) : (
                <div className="anx-scroll">
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, width: '34%' }}>Introducer</th>
                        <th style={th}>Leads</th>
                        <th style={th}>Deals</th>
                        <th style={{ ...th, textAlign: 'right' }}>Generated</th>
                        <th style={{ ...th, textAlign: 'right' }}>You owe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.introducers.map((i, idx) => (
                        <tr key={i.id}>
                          <td style={td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{
                                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                background: idx === 0 ? C.ink : '#EEF1F5',
                                color: idx === 0 ? '#fff' : C.muted,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11, fontWeight: 700,
                              }}>{idx + 1}</span>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: C.ink, overflow: 'hidden',
                                              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.name}</div>
                                {i.company && i.company !== i.name && (
                                  <div style={{ fontSize: 11.5, color: C.faint }}>{i.company}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td style={td}>{nInt(i.leads)}</td>
                          <td style={td}>{nInt(i.deals)}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700,
                                       color: i.revenueBanked ? C.green : C.faint }}>
                            {gbp(i.revenueBanked)}
                            {i.revenuePipeline > 0 && (
                              <div style={{ fontSize: 11.5, fontWeight: 500, color: C.amber }}>
                                +{gbp(i.revenuePipeline)} open
                              </div>
                            )}
                          </td>
                          <td style={{ ...td, textAlign: 'right', color: i.commissionOwed ? C.ink70 : C.faint }}>
                            {gbp(i.commissionOwed)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card>
              <CardHead title="Why leads died" hint="Recorded reasons for lost and rejected leads." />
              {(data?.lostReasons || []).length === 0 ? (
                <Empty>No leads lost in this period.</Empty>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {(() => {
                    const max = Math.max(1, ...data.lostReasons.map(r => r.count));
                    return data.lostReasons.map(r => (
                      <div key={r.reason}>
                        <div style={{ display: 'flex', justifyContent: 'space-between',
                                      alignItems: 'baseline', marginBottom: 5, gap: 10 }}>
                          <span style={{ fontSize: 13, color: r.reason === 'Not recorded' ? C.faint : C.ink70,
                                         overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.reason}
                          </span>
                          <strong style={{ fontSize: 13.5, color: C.ink }}>{nInt(r.count)}</strong>
                        </div>
                        <Bar2 value={r.count} max={max} colour={r.reason === 'Not recorded' ? '#C9D2DC' : C.red} height={6} />
                      </div>
                    ));
                  })()}
                </div>
              )}
            </Card>
          </div>

          {/* ── Footnote ───────────────────────────────────────────── */}
          <p style={{ marginTop: 24, fontSize: 12, color: C.faint, lineHeight: 1.6, maxWidth: 780 }}>
            <strong style={{ color: C.muted }}>How these are counted.</strong>{' '}
            Revenue banked counts completed deals only, dated by their completion date.
            Open pipeline is a live snapshot of open deals and is not limited to the dates above.
            Conversion is measured on a cohort basis — of the leads received in this period, the share
            that became a deal.
            {data?.meta?.archivedLeadsInPeriod > 0 && !inclArchived && (
              <> {data.meta.archivedLeadsInPeriod} archived {data.meta.archivedLeadsInPeriod === 1 ? 'lead is' : 'leads are'} excluded;
              tick “Include archived” above to count {data.meta.archivedLeadsInPeriod === 1 ? 'it' : 'them'}.</>
            )}
          </p>

        </div>
      </div>
    </div>
  );
}
